import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import client from '@/api/client';
import type { Hostel, Tenant } from '@/api/types';
import { SwapBedPanel } from '@/features/beds/SwapBedPanel';
import { ArrowLeftRight } from 'lucide-react';
import { Select } from '@/components/ui/Select';

export function SwapBedsPage() {
  const { t } = useTranslation();

  // Deep-linked from the Admit Tenant flow when a phone number matches an already-active
  // tenant elsewhere — preselects that tenant instead of starting the swap picker blank.
  const initialSearch = useSearch({ strict: false }) as Partial<{ tenantId: string; hostelId: string }>;

  const { data: hostels = [] } = useQuery<Hostel[]>({
    queryKey: ['my-hostels'],
    queryFn: () => client.get('/api/hostels/my').then(r => r as unknown as Hostel[]),
  });
  const [hostelId, setHostelId] = useState(initialSearch.hostelId ?? '');
  const activeHostel = hostelId || hostels[0]?.id || '';
  const [done, setDone] = useState(false);

  const { data: lockedTenant } = useQuery<Tenant>({
    queryKey: ['tenant', initialSearch.tenantId],
    queryFn: () => client.get(`/api/tenants/${initialSearch.tenantId}`).then(r => r as unknown as Tenant),
    enabled: !!initialSearch.tenantId,
  });

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ArrowLeftRight size={22} className="text-[hsl(var(--primary))]" />
          {t('owner.swapBeds', 'Swap Beds')}
        </h1>
        {hostels.length > 1 && (
          <Select value={hostelId} onChange={e => { setHostelId(e.target.value); setDone(false); }}
            options={hostels.map(h => ({ value: h.id, label: h.name }))} />
        )}
      </div>

      <p className="text-sm text-gray-500 mb-4">
        {t('owner.swapBedsHint', 'Pick two tenants with active beds and swap their room/bed assignments. Rent amounts stay tied to each tenant.')}
      </p>

      {!activeHostel ? (
        <p className="text-gray-400">{t('owner.noHostelsFound', 'No hostels found.')}</p>
      ) : (
        <>
          {done && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">
              {t('owner.bedsSwappedSuccess', 'Beds swapped successfully.')}
            </p>
          )}
          <SwapBedPanel
            key={activeHostel}
            hostelId={activeHostel}
            lockedTenant={lockedTenant?.hostelId === activeHostel ? lockedTenant : undefined}
            bare
            onClose={() => setDone(false)}
            onSuccess={() => setDone(true)}
          />
        </>
      )}
    </div>
  );
}
